import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance
const apiClient = axios.create({
  baseURL: API,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
      // Could redirect to login page here
    }
    return Promise.reject(error);
  }
);

// Authentication
export const authAPI = {
  login: async (username, password) => {
    const response = await apiClient.post('/auth/login', { username, password });
    const { access_token, user_id, role } = response.data;
    
    // Store token and user data
    localStorage.setItem('auth_token', access_token);
    localStorage.setItem('user_data', JSON.stringify({ user_id, role }));
    
    return response.data;
  },
  
  logout: () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
  },
  
  getCurrentUser: async () => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },
  
  isAuthenticated: () => {
    return !!localStorage.getItem('auth_token');
  }
};

// Weather API
export const weatherAPI = {
  getCurrentWeather: async (province = null) => {
    const params = province ? { province } : {};
    const response = await apiClient.get('/weather/current', { params });
    return response.data;
  },
  
  getForecast: async (province, days = 5) => {
    const response = await apiClient.get('/weather/forecast', {
      params: { province, days }
    });
    return response.data;
  },
  
  getHistory: async (province, startDate, endDate) => {
    const response = await apiClient.get('/weather/history', {
      params: {
        province,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString()
      }
    });
    return response.data;
  }
};

// Crop data API
export const cropAPI = {
  getData: async (filters = {}) => {
    const response = await apiClient.get('/crops/data', { params: filters });
    return response.data;
  },
  
  getPredictions: async (filters = {}) => {
    const response = await apiClient.get('/crops/predictions', { params: filters });
    return response.data;
  }
};

// Alerts API
export const alertAPI = {
  getAlerts: async (filters = {}) => {
    const response = await apiClient.get('/alerts', { params: filters });
    return response.data;
  },
  
  deactivateAlert: async (alertId) => {
    const response = await apiClient.put(`/alerts/${alertId}/deactivate`);
    return response.data;
  }
};

// Analytics API
export const analyticsAPI = {
  getSummary: async (year = null) => {
    const params = year ? { year } : {};
    const response = await apiClient.get('/analytics/summary', { params });
    return response.data;
  },
  
  getCorrelation: async (province, cropType, weatherMetric = 'temperature_avg') => {
    const response = await apiClient.get('/analytics/correlation', {
      params: {
        province,
        crop_type: cropType,
        weather_metric: weatherMetric
      }
    });
    return response.data;
  }
};

// Regions API
export const regionAPI = {
  getRegions: async () => {
    const response = await apiClient.get('/regions');
    return response.data;
  }
};

// Export the configured axios instance for custom requests
export { apiClient };

// Auto-login for demo purposes
export const autoLogin = async () => {
  try {
    if (!authAPI.isAuthenticated()) {
      await authAPI.login('admin', 'agricultural_admin_2024');
      console.log('Auto-logged in as admin for demo');
    }
  } catch (error) {
    console.error('Auto-login failed:', error);
  }
};